import React from 'react';
import { Statistic, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SummaryCardsProps } from './types';
import { ICONS } from './constants';
import { SummaryCard, SummaryGrid, StatisticDescription } from './Styles';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatPercentage = (value: number): string => {
  return `${(value || 0).toFixed(1)}%`;
};

const SummaryCards: React.FC<SummaryCardsProps> = ({ summary, loading = false }) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: isDarkMode ? 'dark' : 'light' } as const;

  if (loading || !summary) {
    return (
      <ThemeProvider theme={theme}>
        <SummaryGrid>
          {Array.from({ length: 4 }).map((_, index) => (
            <SummaryCard key={index}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </SummaryCard>
          ))}
        </SummaryGrid>
      </ThemeProvider>
    );
  }

  const feePercentage =
    summary.totalRevenue > 0
      ? (summary.totalFees / summary.totalRevenue) * 100
      : 0;

  return (
    <ThemeProvider theme={theme}>
      <SummaryGrid>
        <SummaryCard>
          <Statistic
            title="Total Revenue"
            value={summary.totalRevenue}
            prefix={<FontAwesomeIcon icon={ICONS.dollar} />}
            formatter={(value) => {
              const numValue = Number(value);
              return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }}
            valueStyle={{ color: isDarkMode ? '#ffffff' : '#262626' }}
          />
          <StatisticDescription>Gross revenue</StatisticDescription>
        </SummaryCard>

        <SummaryCard>
          <Statistic
            title="Total Fees"
            value={summary.totalFees}
            prefix={<FontAwesomeIcon icon={ICONS.dollar} />}
            formatter={(value) => {
              const numValue = Number(value);
              return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }}
            valueStyle={{ color: '#ff4d4f' }}
          />
          <StatisticDescription>{formatPercentage(feePercentage)} of revenue</StatisticDescription>
        </SummaryCard>

        <SummaryCard>
          <Statistic
            title="Net Earnings"
            value={summary.netEarnings}
            prefix={<FontAwesomeIcon icon={ICONS.dollar} />}
            formatter={(value) => {
              const numValue = Number(value);
              return numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }}
            valueStyle={{ color: '#52c41a' }}
          />
          <StatisticDescription>After all fees</StatisticDescription>
        </SummaryCard>

        <SummaryCard>
          <Statistic
            title="Order Count"
            value={summary.orderCount}
            prefix={<FontAwesomeIcon icon={ICONS.shoppingCart} />}
            formatter={(value) => (value || 0).toLocaleString()}
            valueStyle={{ color: isDarkMode ? '#ffffff' : '#262626' }}
          />
          <StatisticDescription>Avg: {formatCurrency(summary.averageOrderValue)}</StatisticDescription>
        </SummaryCard>
      </SummaryGrid>
    </ThemeProvider>
  );
};

export default SummaryCards;
